import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import axiosInstance from "../../../../hooks/axiosInstance";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { COUNTRY_CODES } from "../../../../constants/countryCodes";

export default function EditAccount({ showEdit, closeModal, refreshPage, account }) {
    const [validationError, setValidationError] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [baseUser, setBaseUser] = useState([]);
    const countryCodes = COUNTRY_CODES;

    const [formData, setFormData] = useState({
        base_user_id: "",
        account_id: "",
        company_name: "",
        domain_name: "",
        email: "",
        phone: "",
        country_code: "+1", // Default to USA
    });

    // Fetch base users
    useEffect(() => {
        const fetchBaseUser = async () => {
            setIsLoading(true);
            try {
                const response = await axiosInstance.get(
                    `/user/base_user/fetch_base_user_no_paginate`
                );
                setBaseUser(response.data.data.base_user || []);
            } catch (error) {
                toast.error("Error fetching users:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchBaseUser();
    }, []);

    // Prepopulate form with account data
    useEffect(() => {
        if (account && account.account_phone) {
            // Attempt to extract country code from phone number
            const phone = account.account_phone || "";
            let country_code = "+1"; // Default to USA
            let phone_number = phone;

            // Find matching country code
            const matchedCountry = countryCodes.find((country) =>
                phone.startsWith(country.code)
            );
            if (matchedCountry) {
                country_code = matchedCountry.code;
                phone_number = phone.slice(country_code.length);
            } else if (phone.startsWith("+")) {
                // If no exact match, take the first few characters as country code
                const possibleCode = phone.match(/^\+\d{1,4}/)?.[0];
                if (possibleCode) {
                    country_code = possibleCode;
                    phone_number = phone.slice(possibleCode.length);
                }
            }

            setFormData({
                base_user_id: account.user_id || "",
                account_id: account.account_id || "",
                company_name: account.company_name || "",
                domain_name: account.domain_name || "",
                email: account.account_email || "",
                phone: phone_number,
                country_code,
            });
        }
    }, [account, countryCodes]);

    const resetForm = () => {
        setFormData({
            base_user_id: "",
            account_id: "",
            company_name: "",
            domain_name: "",
            email: "",
            phone: "",
            country_code: "+1", // Reset to USA
        });
        setValidationError({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "phone") {
            // Allow only numbers for phone
            const numericValue = value.replace(/[^0-9]/g, "");
            setFormData((prev) => ({ ...prev, phone: numericValue }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleClose = () => {
        resetForm();
        closeModal();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            // Combine country code and phone number for submission
            const payload = {
                ...formData,
                phone: `${formData.country_code}${formData.phone}`,
            };

            await axiosInstance.post(`/user/account/update_account`, payload);
            toast.success("Account updated successfully!");
            refreshPage();
            handleClose();
        } catch (error) {
            if (error.response && error.response.data.errors) {
                setValidationError(error.response.data.errors);
            } else {
                toast.error("An error occurred while updating the account.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="CreateUser">
            <Modal show={showEdit} onHide={handleClose}>
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit User Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="input-group mb-2">
                            <div className="input-group-prepend">
                                <span className="input-group-text">
                                    Base user
                                </span>
                            </div>
                            <select
                                className="custom-select form-control"
                                name="base_user_id"
                                value={formData.base_user_id}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        base_user_id: e.target.value,
                                    }))
                                }
                            >
                                <option value="">-- Select Customer --</option>
                                {baseUser.map((user) => (
                                    <option key={user.id} value={user.id}>
                                        {user.first_name} {user.last_name}
                                    </option>
                                ))}
                            </select>
                            {validationError.base_user_id && (
                                <small className="text-danger my-1">
                                    {validationError.base_user_id[0]}
                                </small>
                            )}
                        </div>
                        <br />
                        <Form.Group className="mb-3" controlId="formCompanyName">
                            <div>
                                <h6>Company Name</h6>
                            </div>
                            <Form.Control
                                type="text"
                                placeholder="Enter..."
                                name="company_name"
                                value={formData.company_name}
                                onChange={handleChange}
                                required
                            />
                            {validationError.company_name && (
                                <small className="text-danger my-1">
                                    {validationError.company_name[0]}
                                </small>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formDomainName">
                            <div>
                                <h6>Domain Name</h6>
                            </div>
                            <Form.Control
                                type="text"
                                placeholder="Enter..."
                                name="domain_name"
                                value={formData.domain_name}
                                onChange={handleChange}
                                required
                            />
                            {validationError.domain_name && (
                                <small className="text-danger my-1">
                                    {validationError.domain_name[0]}
                                </small>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formEmail">
                            <div>
                                <h6>Email</h6>
                            </div>
                            <Form.Control
                                type="email"
                                placeholder="Enter..."
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                            {validationError.email && (
                                <small className="text-danger my-1">
                                    {validationError.email[0]}
                                </small>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formPhone">
                            <div>
                                <h6>Phone</h6>
                            </div>
                            <div className="input-group">
                                <select
                                    name="country_code"
                                    value={formData.country_code}
                                    onChange={handleChange}
                                    className="form-control"
                                    style={{ width: "40%" }}
                                >
                                    {countryCodes.length === 0 ? (
                                        <option>Loading...</option>
                                    ) : (
                                        countryCodes.map((country) => (
                                            <option key={country.code} value={country.code}>
                                                {country.label}
                                            </option>
                                        ))
                                    )}
                                </select>
                                <Form.Control
                                    type="tel"
                                    placeholder="Enter phone number..."
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                    className="form-control"
                                    style={{ width: "60%" }}
                                />
                            </div>
                            {validationError.phone && (
                                <small className="text-danger my-1">
                                    {validationError.phone[0]}
                                </small>
                            )}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            style={{ backgroundColor: "#c4281b", color: "#fff" }}
                            type="submit"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />{" "}
                                    Saving...
                                </>
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}